// src/templates/modern/restaurant/components/BranchFeedback.tsx
import React, { useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaStar, FaShieldAlt, FaRegSmile } from 'react-icons/fa';
import { useBranch } from '~/contexts/BranchContext';
import FeedbackModal from '~/components/feedback/FeedbackModal';
import FeedbackCongratulations from '~/components/feedback/FeedbackCongratulations';

const BranchFeedback = () => {
  const { t, i18n } = useTranslation();
  const { branch } = useBranch();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackCongratulations, setShowFeedbackCongratulations] = useState(false);
  const [initialRating, setInitialRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  if (!branch) return null;

  const handleStarClick = (rating: number) => {
    setInitialRating(rating);
    setShowFeedbackModal(true);
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="relative overflow-hidden rounded-3xl p-8 md:p-14 text-center cursor-default"
        >
           {/* Card Background Layer */}
           <div 
             className="absolute inset-0 z-0 transition-opacity duration-300"
             style={{
               backgroundColor: branch.theme.cardColor,
               boxShadow: `0 30px 60px -15px ${branch.theme.primaryColor}20, 0 0 0 1px ${branch.theme.primaryColor}10`,
             }}
           />

          {/* Pulsing Decorative Background Elements */}
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.03, 0.06, 0.03],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut" 
            }}
            className="absolute top-0 left-0 w-80 h-80 rounded-full blur-[60px] -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
            style={{ backgroundColor: branch.theme.primaryColor }}
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.03, 0.05, 0.03],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1 
            }}
            className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-[60px] translate-x-1/2 translate-y-1/2 pointer-events-none z-0"
            style={{ backgroundColor: branch.theme.primaryColor }}
          />

          {/* Content */}
          <div className="relative z-10 space-y-10">
            <motion.div variants={itemVariants} className="space-y-4">
              <h2 
                className="text-3xl md:text-4xl font-bold tracking-tight"
                style={{ color: branch.theme.textColor }}
              >
                {t('feedback.title', 'How was your experience?')}
              </h2>
              <p 
                className="text-base md:text-lg opacity-80 max-w-lg mx-auto leading-relaxed"
                style={{ color: branch.theme.textColor }}
              >
                {t('feedback.subtitle', 'Your feedback helps us serve you better.')}
              </p>
            </motion.div>

            {/* Interactive Stars */}
            <motion.div variants={itemVariants} className="flex justify-center items-center gap-3 md:gap-6 py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <motion.button
                  key={star}
                  onClick={() => handleStarClick(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  whileHover={{ scale: 1.25, rotate: 10, y: -5 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 + star * 0.1, type: "spring" }}
                  className="relative p-2 focus:outline-none group"
                >
                  <FaStar
                    size={40}
                    className={`transition-all duration-300 drop-shadow-md ${
                      star <= (hoverRating || 0) 
                        ? 'text-yellow-400' 
                        : 'text-gray-200'
                    }`}
                    style={{
                      fill: star <= (hoverRating || 0) ? '#facc15' : 'currentColor',
                      color: star <= (hoverRating || 0) ? '#facc15' :  `${branch.theme.textColor}10`
                    }}
                  />
                  {/* Glow effect on hover */}
                  <div className={`absolute inset-0 bg-yellow-400/30 blur-xl rounded-full transition-opacity duration-300 ${star <= hoverRating ? 'opacity-100' : 'opacity-0'}`} />
                </motion.button>
              ))}
            </motion.div>

            {/* Helper Text / CTA */}
            <motion.div variants={itemVariants}>
              <motion.button
                onClick={() => {
                  setInitialRating(0);
                  setShowFeedbackModal(true);
                }}
                whileHover={{ scale: 1.05, backgroundColor: `${branch.theme.primaryColor}15` }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold transition-colors"
                style={{ 
                  color: branch.theme.primaryColor,
                  backgroundColor: `${branch.theme.primaryColor}08`
                }}
              >
                <span>{t('feedback.leaveDetailedReview', 'Leave a detailed review')}</span>
              </motion.button>
            </motion.div>
           
            {/* Trust Badges */}
            <motion.div variants={itemVariants} className="pt-6 flex flex-wrap justify-center gap-x-8 gap-y-4 opacity-50 hover:opacity-80 transition-opacity duration-300">
              <div 
                className="flex items-center gap-2 text-xs md:text-sm font-medium px-4 py-2 rounded-full border border-current"
                style={{ color: branch.theme.textColor, borderColor: 'currentColor' }}
              >
                <FaShieldAlt className="text-sm" />
                <span>{t('feedback.anonymous', '100% Anonymous')}</span>
              </div>
              <div 
                className="flex items-center gap-2 text-xs md:text-sm font-medium px-4 py-2 rounded-full border border-current"
                style={{ color: branch.theme.textColor, borderColor: 'currentColor' }}
              >
                <FaRegSmile className="text-sm" />
                <span>{t('feedback.valued', 'We value your opinion')}</span>
              </div>
            </motion.div>

            
         
        
          </div>
        </motion.div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        show={showFeedbackModal}
        initialRating={initialRating}
        onClose={() => setShowFeedbackModal(false)}
        onSuccess={() => setShowFeedbackCongratulations(true)}
      />

      {/* Feedback Congratulations */}
      <FeedbackCongratulations
        show={showFeedbackCongratulations}
        onClose={() => setShowFeedbackCongratulations(false)}
        duration={5000}
      />
    </>
  );
};

export default BranchFeedback;